import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './styles/tokens.css';
import './styles/app.css';
import './styles/parts.css';
import './styles/screens.css';
import { App } from './App.js';
import { I18nProvider } from './core/i18n/index.js';
import { AuthProvider, useAuth } from './core/state/auth.js';
import { SettingsProvider } from './core/state/settings.js';
import { ToastProvider } from './core/state/toast.js';
import { SyncProvider } from './core/sync.js';
import { IdleSignOut } from './core/state/idle.js';
import { MoveNotice } from './core/ui/MoveNotice.js';
import { SetupGate } from './modules/admin/Setup.js';
import { moveIfRehomed } from './core/move.js';
import { installErrorReporting } from './core/errors.js';
import { ErrorBoundary } from './core/ui/ErrorBoundary.js';

/**
 * The owner's panel is its own app. Loaded only when /platform is opened, so a
 * till never downloads it, and none of the till's machinery - the shop, the
 * offline copy, the sync - runs behind it.
 */
const PlatformApp = lazy(() => import('./platform/PlatformApp.js'));
const isPlatform = window.location.pathname === '/platform' || window.location.pathname.startsWith('/platform/');

// The panel keeps nothing waiting on the device, so it can always move at once.
if (isPlatform) void moveIfRehomed(async () => 0);

installErrorReporting(isPlatform ? 'panel' : 'till');

/** Syncing only starts once somebody is signed in. */
function WithSync({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return <SyncProvider signedIn={!!user}>{children}</SyncProvider>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <I18nProvider>
        <ToastProvider>
          {isPlatform ? (
            <ErrorBoundary home="/platform">
              <Suspense fallback={null}>
                <PlatformApp />
              </Suspense>
            </ErrorBoundary>
          ) : (
            <ErrorBoundary>
              <AuthProvider>
                <SettingsProvider>
                  <MoveNotice />
                  <IdleSignOut />
                  <SetupGate />
                  <WithSync>
                    <App />
                  </WithSync>
                </SettingsProvider>
              </AuthProvider>
            </ErrorBoundary>
          )}
        </ToastProvider>
      </I18nProvider>
    </BrowserRouter>
  </StrictMode>,
);
